import type {
  CameraLike,
  CameraOptions,
  EulerRotation,
  ProjectedPoint,
  ScreenshotOptions,
  Transform,
  Track,
} from './types';
import type { SampleAnimTrackRouteOptions } from './track-route';
import { Vec3, type Vec3Like } from './vector';

// ---------------------------------------------------------------------------
// Viewer — 面向业务的"场景闭环"契约
//
// 与 RendererAdapter（渲染后端内部契约）不同，Viewer 表达的是完整体验闭环：
// 业务一次性输入场景参数（splat url、collision、运镜轨迹、热点标注），
// viewer 内部闭环执行（导航/碰撞/运镜/拾取）；业务侧只通过只读查询、
// 意图式命令与事件回流和场景交互，不逐帧驱动相机。
//
// 每个渲染包（playcanvas / three / …）按此契约提供 createViewer() 实现。
// ---------------------------------------------------------------------------

export type ViewerNavigationMode = 'orbit' | 'fly' | 'walk' | 'avatar' | 'anim';

/** 移动端漫游移动意图，取值 -1~1 表示归一化方向强度。 */
export interface WalkMoveIntent {
  /** 前进(+1) / 后退(-1) */
  forward: number;
  /** 右移(+1) / 左移(-1) */
  right: number;
}

export type ViewerInputMode = 'desktop' | 'touch';

export interface JoystickMoveIntent {
  x: number;
  y: number;
  sprint?: boolean;
  /** 飞行模式高度升降：正值上升，负值下降，取值 -1~1 */
  altitude?: number;
}

export interface AvatarAnimationMapping {
  idle: string;
  run: string;
  sprint: string;
  jumpIdle: string;
  jumpRun: string;
  fall: string;
  rotateLeft: string;
  rotateRight: string;
}

export interface ViewerAvatarState {
  enabled: boolean;
  available: boolean;
  loading: boolean;
  url: string;
  status: string;
  animations: string[];
  currentAnimation: string;
  followCamera: boolean;
}

export interface ViewerInitOptions {
  /** CSS hex 背景色 */
  background?: string;
  /** 初始相机；loadScene 的 settings.cameras 会覆盖 */
  camera?: CameraOptions;
  /** 渲染后端偏好（playcanvas 实现支持 webgpu 回退 webgl） */
  renderer?: 'webgl' | 'webgpu';
  /** 抗锯齿 splat shader 开关 */
  aa?: boolean;
  /** 等待全部流式 LOD 数据后才视为 ready */
  fullLoad?: boolean;
  /** splat 预算（百万），覆盖默认值 */
  splatBudget?: number;
  /** 显示运行时性能浮层（MiniStats，FPS/GPU/显存/splat 数），调试用 */
  ministats?: boolean;
  /** 禁用 viewer 自身输入监听，适用于只读预览实例 */
  inputEnabled?: boolean;
  avatarUrl?: string;
  avatarEnabled?: boolean;
  avatarFollowCamera?: boolean;
  avatarMapping?: Partial<AvatarAnimationMapping>;
}

export type ViewerAnimTrack = Track;

/** 热点标注；position 为世界空间锚点（与 splat 应用 baseRotation 后同空间）。 */
export interface ViewerAnnotation {
  position: Vec3Like;
  title: string;
  text?: string;
  /** 飞行目标相机；缺省时由 viewer 基于当前相机推导环视机位 */
  camera?: CameraLike;
}

export interface ViewerSceneOptions {
  url: string;
  /** 文件名提示，用于 blob URL 等无扩展名场景的格式检测 */
  filename?: string;
  /** SOG 内联 JSON 描述符，传入时优先于从 url 下载 */
  sogMeta?: object;
  /**
   * ExperienceSettings 结构透传（运镜/热点/后效/初始相机），viewer 闭环消费。
   * annotations / animTrack / autoPlayAnim 字段会覆盖 settings 内对应内容。
   */
  settings?: object;
  /** 覆盖/补充 settings.annotations（业务侧 fallback 热点数据走这里） */
  annotations?: ViewerAnnotation[];
  /** 覆盖/补充 settings.animTracks[0]（mock 轨迹、调试输入走这里） */
  animTrack?: ViewerAnimTrack;
  /** 是否以运镜开场（覆盖 settings.startMode） */
  autoPlayAnim?: boolean;
  skyboxUrl?: string;
  collisionUrl?: string;
  collisionBinUrl?: string;
  /** 跳过默认平地碰撞 fallback（文件拖拽等无碰撞数据场景） */
  skipDefaultCollision?: boolean;
  /** 跳过默认运镜 fallback（文件拖拽等无轨迹场景，不生成旋转/8 字默认 track） */
  skipDefaultTrack?: boolean;
  /** 开启碰撞体可视化（调试用；实现可不支持） */
  debugCollision?: boolean;
  debugTrackRoute?: boolean;
  transform?: Transform;
  /** 场景级基础旋转（弧度），用于源数据 up-axis 修正（如 Z-up 转 Y-up） */
  baseRotation?: EulerRotation;
}

export interface ViewerPickResult {
  point: Vec3;
}

export interface ViewerAnimState {
  hasAnimation: boolean;
  /** 秒 */
  duration: number;
  time: number;
  paused: boolean;
  /** 相机当前是否由运镜驱动（用户拖拽中断后为 false） */
  active: boolean;
}

export interface ViewerAvatarFallEvent {
  mode: Extract<ViewerNavigationMode, 'walk' | 'avatar'>;
  position: Vec3Like;
  lastSafeY: number;
  dropDistance: number;
}

export interface ViewerEventMap {
  /** 加载进度 0–100 */
  progress: (progress: number) => void;
  ready: () => void;
  error: (err: unknown) => void;
  'navigationMode:changed': (mode: ViewerNavigationMode) => void;
  /** 运镜时间/暂停/激活状态任一变化时触发 */
  'anim:changed': (state: ViewerAnimState) => void;
  /** 标注被激活（用户聚焦或 focusAnnotation 调用），index 对应 getAnnotations() 顺序 */
  'annotation:activate': (index: number) => void;
  'measure:changed': (active: boolean) => void;
  'avatar:changed': (state: ViewerAvatarState) => void;
  'avatar:fallen': (event: ViewerAvatarFallEvent) => void;
  /** 模型列表变化（新增/删除/清空） */
  'model:changed': (models: ViewerModelHandle[]) => void;
  /** 单个模型 transform 变化 */
  'model:transform': (event: ViewerModelTransformEvent) => void;
  /** 相机 gizmo 拖拽产生新位置时触发 */
  'cameraGizmo:transform': (position: Vec3Like) => void;
}

export interface ViewerLifecycleApi {
  init(container: HTMLElement, options?: ViewerInitOptions): Promise<void>;
  loadScene(options: ViewerSceneOptions): Promise<void>;
  resize(width: number, height: number): void;
  flushFrame(deltaTime?: number): void;
  dispose(): void;
}

export interface ViewerEventApi {
  on<K extends keyof ViewerEventMap>(event: K, cb: ViewerEventMap[K]): void;
  off<K extends keyof ViewerEventMap>(event: K, cb: ViewerEventMap[K]): void;
}

export interface ViewerCameraApi {
  get(): CameraOptions;
  /** 调试/初始化用途；运行期相机控制应交给 viewer 闭环 */
  set(camera: CameraOptions): void;
  /** GPU/几何拾取场景表面点；无命中返回 null */
  pick(options: { clientX: number; clientY: number }): Promise<ViewerPickResult | null>;
  /** 世界点 → 视口投影；返回 client 坐标 */
  project(point: Vec3Like): ProjectedPoint | null;
  captureScreenshot(options?: ScreenshotOptions): Promise<Blob>;
}

export interface ViewerWalkApi {
  setMoveIntent(intent: WalkMoveIntent): void;
  setJoystickIntent(intent: JoystickMoveIntent): void;
  jump(): void;
}

export interface ViewerAnimApi {
  getState(): ViewerAnimState;
  play(): void;
  pause(): void;
  /** 与 Space 快捷键相同语义：anim 模式下 toggle pause，非 anim 模式切入播放。 */
  togglePlayPause(): void;
  seek(time: number): void;
  /** 播放倍速；实现不支持时为 no-op */
  setSpeed(speed: number): void;
  /** 运行期动态替换运镜轨迹，传 null 则清空运镜动画 */
  setTrack(track: ViewerAnimTrack | null): void;
}

export interface ViewerNavigationApi {
  getMode(): ViewerNavigationMode;
  setMode(mode: ViewerNavigationMode): void;
  reset(): void;
  isWalkAvailable(): boolean;
  walk: ViewerWalkApi;
  anim: ViewerAnimApi;
}

export interface ViewerSceneApi {
  getAnnotations(): ViewerAnnotation[];
  /** 平滑飞到指定标注的机位 */
  focusAnnotation(index: number): void;
  /** 显示/隐藏运镜路径轨迹线；传 null 清除 */
  showTrackRoute?(points: Vec3Like[] | null): void;
  showCameraTrajectory?(poses: CameraLike[] | null): void;
  showAnimTrackRoute?(track: ViewerAnimTrack | null, options?: SampleAnimTrackRouteOptions): void;
  showPreviewCameraPose?(pose: CameraLike | null): void;
  /** 在指定相机位姿处显示/移除 gizmo；pose 为 null 时移除。拖拽时触发 cameraGizmo:transform 事件 */
  setCameraGizmo?(pose: CameraLike | null, mode?: GizmoMode): void;
}

export interface ViewerMeasureApi {
  isActive(): boolean;
  setActive(active: boolean): void;
  toggle(): boolean;
  clear(): void;
  setUnitSystem(system: 'metric' | 'imperial'): void;
}

export interface ViewerAvatarApi {
  getState(): ViewerAvatarState | null;
  setEnabled(enabled: boolean): void;
  setUrl(url: string): void;
  setFollowCamera(enabled: boolean): void;
  setMapping(mapping: Partial<AvatarAnimationMapping>): void;
}

export interface ViewerInputApi {
  setMode(mode: ViewerInputMode): void;
  setGamingControls(enabled: boolean): void;
}

/** XR 性能档位：从极低到极致，控制 framebufferScale、splatBudget、fixedFoveation 等。 */
export type VrQualityPreset = 'lowest' | 'low' | 'medium' | 'high' | 'higher' | 'highest';

export interface ViewerXrApi {
  enterVr(preset?: VrQualityPreset): void;
}

// ---------------------------------------------------------------------------
// 模型导入 — GLB/GLTF 等网格模型放置与 transform 编辑
// ---------------------------------------------------------------------------

/** 已导入模型的只读句柄，携带当前 transform 快照。 */
export interface ViewerModelHandle {
  id: string;
  name: string;
  transform: Transform;
}

/** model:transform 事件载荷。 */
export interface ViewerModelTransformEvent {
  modelId: string;
  transform: Transform;
}

/** gizmo 操作模式 */
export type GizmoMode = 'translate' | 'rotate' | 'scale';

export interface ViewerModelApi {
  /** 导入 GLB/GLTF 模型；url 或 file 二选一。返回模型句柄。 */
  addModel(options: { url?: string; file?: File; name?: string }): Promise<ViewerModelHandle>;
  /** 移除指定模型 */
  removeModel(modelId: string): void;
  /** 复制指定模型（重新导入相同资源，position 偏移 1 单位），返回新句柄 */
  duplicateModel(modelId: string): Promise<ViewerModelHandle>;
  /** 设置模型 transform（position/rotation/scale） */
  setModelTransform(modelId: string, transform: Transform): void;
  /** 读取模型当前 transform */
  getModelTransform(modelId: string): Transform | null;
  /** 获取所有已导入模型句柄列表 */
  getModels(): ViewerModelHandle[];
  /** 在屏幕坐标处拾取模型；命中返回 modelId，否则 null */
  pickModel(options: { clientX: number; clientY: number }): Promise<{ modelId: string } | null>;
  /** 为指定模型附加/切换/移除 gizmo；mode 为 null 时移除 */
  setGizmo(modelId: string, mode: GizmoMode | null): void;
  /** 聚焦相机到指定模型，自动计算距离使整个 bounding box 可见 */
  focusModel(modelId: string): void;
  /** 设置模型编辑模式；激活时屏蔽 splat 点击导航 */
  setEditMode(active: boolean): void;
}

export interface Viewer extends ViewerLifecycleApi, ViewerEventApi {
  readonly camera: ViewerCameraApi;
  readonly navigation: ViewerNavigationApi;
  readonly scene: ViewerSceneApi;
  readonly measure: ViewerMeasureApi;
  readonly avatar: ViewerAvatarApi;
  readonly input: ViewerInputApi;
  readonly xr: ViewerXrApi;
  readonly model: ViewerModelApi;
}

// ---------------------------------------------------------------------------
// 共享推导逻辑
// ---------------------------------------------------------------------------

/**
 * 为缺省 camera 的标注推导环视机位：保持当前相机方向，
 * 从锚点反向退到合适距离，确保有自然的俯视角。
 */
export function deriveAnnotationCamera(
  annotation: ViewerAnnotation,
  current?: CameraLike,
): CameraOptions {
  if (annotation.camera) {
    return {
      position: annotation.camera.position,
      target: annotation.camera.target,
      fov: annotation.camera.fov ?? current?.fov,
      near: current?.near,
      far: current?.far,
    };
  }

  const target = Vec3.from(annotation.position);
  const currentPosition = current?.position ?? target.add({ x: 0, y: 1.2, z: 3 });
  const direction = Vec3.from(currentPosition).sub(target).normalize({ x: 0, y: 0.35, z: 1 });
  const distance = Math.max(Vec3.from(currentPosition).distanceTo(target), 2.5);

  return {
    position: target.add(direction.scale(distance)),
    target: target.toObject(),
    fov: current?.fov,
    near: current?.near,
    far: current?.far,
  };
}
